import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';
import log from '../../logger';

// Fetch threads with pagination & filters
export const fetchThreads = createAsyncThunk(
  'thread/fetchThreads',
  async ({ page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Fetching threads with page:', page, 'and limit:', limit);
      const response = await api.threads.getAll({ page, limit, filters });
      if (response.data.status) {
        log.info('Threads fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch threads:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching threads:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching threads');
    }
  }
);

// Search threads with keyword
export const searchThreads = createAsyncThunk(
  'thread/searchThreads',
  async ({ keyword, page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Searching threads with keyword:', keyword);
      const response = await api.threads.search({ keyword, page, limit, ...filters });
      if (response.data.status) {
        log.info('Threads search completed successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to search threads:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error searching threads:', error);
      return rejectWithValue(error.response?.data?.message || 'Error searching threads');
    }
  }
);

// Create thread
export const createThread = createAsyncThunk(
  'thread/createThread',
  async (formData, { rejectWithValue }) => {
    try {
      log.info('Creating thread with data:', formData);
      const response = await api.threads.create(formData);
      if (response.data.status) {
        log.info('Thread created successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to create thread:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error creating thread:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to create thread');
    }
  }
);

// Update thread
export const updateThread = createAsyncThunk(
  'thread/updateThread',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      log.info('Updating thread with data:', formData);
      const response = await api.threads.update(id, formData);
      if (response.data.status) {
        log.info('Thread updated successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to update thread:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating thread:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to update thread');
    }
  }
);

// Delete thread
export const deleteThread = createAsyncThunk(
  'thread/deleteThread',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Deleting thread with ID:', id);
      const response = await api.threads.delete(id);
      if (response.data.status) {
        log.info('Thread deleted successfully:', id);
        return id;
      } else {
        log.warn('Failed to delete thread:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error deleting thread:', error);
      return rejectWithValue(error.response?.data?.message || 'Error deleting thread');
    }
  }
);

// Fetch a single thread by ID
export const fetchThreadById = createAsyncThunk(
  'thread/fetchThreadById',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Fetching thread with ID:', id);
      const response = await api.threads.getById(id);
      if (response.data.status) {
        log.info('Thread fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch thread:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching thread:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching thread');
    }
  }
);

// Bulk delete threads
export const bulkDeleteThreads = createAsyncThunk(
  'thread/bulkDeleteThreads',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.threads.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting threads');
    }
  }
);

// Bulk activate/deactivate threads
export const bulkUpdateThreadStatus = createAsyncThunk(
  'thread/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      await api.threads.bulkUpdateStatus(ids, status);
      return { ids, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error updating thread status');
    }
  }
);

// Download TikTok video
export const downloadTikTokVideo = createAsyncThunk(
  'thread/downloadTikTokVideo',
  async (videoData, { rejectWithValue }) => {
    try {
      const response = await api.tiktok.downloadVideo(videoData);
      
      if (response.data.success) {
        return response.data;
      } else {
        return rejectWithValue(response.data.error);
      }
    } catch (error) {
      log.error('Error downloading TikTok video:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to download TikTok video');
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  page: 1,
  pages: 0,
  limit: 10,
  loading: false,
  downloadLoading: false,
  error: null,
  downloadError: null,
};

const threadSlice = createSlice({
  name: 'thread',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch threads
      .addCase(fetchThreads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchThreads.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.threads || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
      })
      .addCase(fetchThreads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Search threads
      .addCase(searchThreads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchThreads.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.threads || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
      })
      .addCase(searchThreads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create thread
      .addCase(createThread.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createThread.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items.push(action.payload);
      })
      .addCase(createThread.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update thread
      .addCase(updateThread.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateThread.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateThread.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete thread
      .addCase(deleteThread.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteThread.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.filter(item => item._id !== action.payload);
      })
      .addCase(deleteThread.rejected, (state) => {
        state.loading = false;
      })
      // Bulk delete threads
      .addCase(bulkDeleteThreads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteThreads.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteThreads.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkUpdateThreadStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateThreadStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { ids, status } = action.payload;
        state.items = state.items.map(item => ids.includes(item._id) ? { ...item, status } : item);
      })
      .addCase(bulkUpdateThreadStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Download TikTok video
      .addCase(downloadTikTokVideo.pending, (state) => {
        state.downloadLoading = true;
        state.downloadError = null;
      })
      .addCase(downloadTikTokVideo.fulfilled, (state) => {
        state.downloadLoading = false;
        state.downloadError = null;
      })
      .addCase(downloadTikTokVideo.rejected, (state, action) => {
        state.downloadLoading = false;
        state.downloadError = action.payload;
      });
  },
});

export default threadSlice.reducer; 